/**
 * CX kompositt-veiviser (Trinn D) — smart default-engine.
 *
 * Gir forslag til composite_mode + vekter basert på hvilke komponent-typer
 * forfatter har valgt. Preset-matrisen under er bekreftet av Kent 2026-11-01.
 * Brukeren kan alltid overstyre forslaget i UI.
 */
import type { CompositeComponentType, CompositeMode } from '../types/exam';
import type { WizardComponentDraft } from '../components/wizards/NewExamSetWizard';

export interface CompositeSuggestion {
  mode: NonNullable<CompositeMode>;
  weights: Partial<Record<CompositeComponentType, number>>;
  reason: string;
}

const PRACTICAL_ALL_REQ: CompositeComponentType[] = ['practical', 'observation', 'portfolio'];

/**
 * Beregn anbefalt modus + vekter for en gitt kombinasjon av komponent-typer.
 *
 * Matrise (Kent 2026-11-01):
 *   written + project         → weighted 60/40
 *   written + oral            → weighted 70/30
 *   written + practical       → weighted 50/50
 *   practical + observation   → all_required
 *   portfolio alene           → all_required (ikke relevant, men beholder konsistens)
 *   3+ komponenter            → all_required
 *   Default (uklart par)      → all_required
 */
export function suggestCompositeDefaults(types: CompositeComponentType[]): CompositeSuggestion {
  const set = new Set(types);
  const n = types.length;

  // 3+ komponenter → alle må bestås (regel 4)
  if (n >= 3) {
    return {
      mode: 'all_required_composite',
      weights: {},
      reason: '3 eller flere komponenter — hver må bestås separat',
    };
  }

  // Praktisk-tunge kombinasjoner (regel 3)
  if (n === 2 && PRACTICAL_ALL_REQ.some(t => set.has(t)) &&
      types.every(t => PRACTICAL_ALL_REQ.includes(t) || t === 'oral' || t === 'group')) {
    return {
      mode: 'all_required_composite',
      weights: {},
      reason: 'Praktisk/observasjon/portfolio — alle må bestås',
    };
  }

  // Vektet-regler (regel 1-2)
  if (set.has('written') && set.has('project')) {
    return {
      mode: 'weighted_composite',
      weights: { written: 60, project: 40 },
      reason: 'Skriftlig + prosjekt: tradisjonell vekting 60/40',
    };
  }
  if (set.has('written') && set.has('oral')) {
    return {
      mode: 'weighted_composite',
      weights: { written: 70, oral: 30 },
      reason: 'Skriftlig + muntlig: tradisjonell vekting 70/30',
    };
  }
  if (set.has('written') && set.has('practical')) {
    return {
      mode: 'weighted_composite',
      weights: { written: 50, practical: 50 },
      reason: 'Skriftlig + praktisk: likevekt 50/50',
    };
  }

  // Fallback for alle andre to-komponent-kombinasjoner
  return {
    mode: 'all_required_composite',
    weights: {},
    reason: 'Kombinasjon uten etablert konvensjon — alle må bestås',
  };
}

/**
 * Anvend forslaget på en komponent-liste. Beholder brukerens rekkefølge,
 * fyller inn weight_pct fra forslaget hvis matched. Hvis forslaget er
 * all_required, nullstilles weight_pct.
 */
export function redistributeWeights(
  components: WizardComponentDraft[],
  suggestion: CompositeSuggestion
): WizardComponentDraft[] {
  if (suggestion.mode === 'all_required_composite') {
    return components.map(c => ({ ...c, weight_pct: null }));
  }
  // weighted: fyll inn fra matrisen, eller fordel jevnt hvis typen ikke er dekket
  const mapped = components.map(c => ({
    ...c,
    weight_pct: suggestion.weights[c.component_type] ?? null,
  }));
  const total = mapped.reduce((s, c) => s + (c.weight_pct || 0), 0);
  if (total === 100) return mapped;
  // Hvis sum != 100 (fordi matrisen ikke dekker alle), fordel jevnt
  const even = Math.floor(100 / mapped.length);
  const remainder = 100 - even * mapped.length;
  return mapped.map((c, i) => ({
    ...c,
    weight_pct: even + (i === 0 ? remainder : 0),
  }));
}
